import { PhoneCall } from 'lucide-react';
import { useState } from 'react';
import { useAuth } from '@/auth/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { updateSiteSettings } from '@/data/settingsRepo';
import { useSiteSettings } from '@/hooks/useSiteSettings';
import { formatDate } from '@/util/util_format';

/**
 * The number a guest rings on the day.
 *
 * It is printed on the recap sheet attached to every booking confirmation, and
 * nowhere else yet — which is the point worth making on screen, because it means
 * a change here reaches confirmations sent from now on and cannot reach the
 * sheets already in guests' hands.
 *
 * Stored rather than compiled in for the ordinary reason: which phone is staffed
 * changes with the rota, not with a release.
 */
export function EmergencyContactCard() {
  const { settings, loading } = useSiteSettings();

  return (
    <section className='tw:rounded-lg tw:border tw:bg-card tw:p-6'>
      <div className='tw:flex tw:items-center tw:gap-3'>
        <PhoneCall className='tw:size-5 tw:text-primary' />
        <div>
          <p className='tw:font-medium'>Emergency contact</p>
          <p className='tw:text-sm tw:text-muted-foreground'>
            Printed on the guest sheet attached to every booking confirmation, for guests who cannot
            find the venue or are running late.
          </p>
        </div>
      </div>

      {loading
        ? <p className='tw:mt-6 tw:text-sm tw:text-muted-foreground'>Loading…</p>
        // Mounted only once the stored value is in, so the field initialises from
        // it and is then the typist's alone — the subscription deliberately does
        // not flow into it afterwards. Same reasoning as `InvoicingSettingsCard`.
        : <EmergencyContactForm initial={settings.emergencyPhone} updatedAt={settings.updatedAt} />}
    </section>
  );
}

function EmergencyContactForm({ initial, updatedAt }: { initial: string; updatedAt?: string }) {
  const { user } = useAuth();
  const [phone, setPhone] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      // Trimmed on the way in, because blank is a meaningful state — it is what
      // keeps the section off the sheet — and a field holding a space is not it.
      await updateSiteSettings({ emergencyPhone: phone.trim() }, user?.uid);
      setSaved(true);
    } catch (e) {
      console.error('Failed to save the emergency contact', e);
      setError('Could not save that. Check your connection and try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <div className='tw:mt-6 tw:max-w-sm tw:space-y-2'>
        <Label htmlFor='site-emergency-phone'>Emergency phone number</Label>
        <Input
          id='site-emergency-phone'
          type='tel'
          value={phone}
          placeholder='+81 90 1234 5678'
          onChange={(e) => {
            setPhone(e.target.value);
            setSaved(false);
          }}
        />
        <p className='tw:text-xs tw:text-muted-foreground'>
          Printed exactly as typed, so write it as a guest should dial it — in full international
          form for anyone calling from a foreign handset. Leave blank to keep it off the sheet
          entirely.
        </p>
      </div>

      <div className='tw:mt-6 tw:flex tw:flex-wrap tw:items-center tw:gap-4'>
        <Button onClick={() => void handleSave()} disabled={saving}>
          {saving ? 'Saving…' : 'Save emergency number'}
        </Button>
        {saved && (
          <span className='tw:text-sm tw:text-muted-foreground'>
            Saved. Confirmations sent from now on will carry it.
          </span>
        )}
        {updatedAt && !saved && (
          <span className='tw:text-xs tw:text-muted-foreground'>
            Last changed {formatDate(updatedAt)}.
          </span>
        )}
      </div>

      {error && <p className='tw:mt-3 tw:text-sm tw:text-ed-red'>{error}</p>}
    </>
  );
}
